import { useCallback, useEffect, useRef } from "react";

import {
  resolveStripeElementsCustomerSessionClientSecret,
  resolveStripeElementsPaymentMethodTypes,
  resolveStripeElementsSetupFutureUsage,
} from "@/react/components/Paywalls/StripeElements/helpers";
import { type StripeElementsUpdateOptions } from "@/react/components/Paywalls/StripeElements/types";
import { addBugsnagBreadcrumb } from "@/react/helpers/bugsnagHelpers";

import { CheckoutError } from "../../CheckoutError";
import {
  useGetStripeElements,
  useGetStripeElementsOrNull,
} from "../../useStripeElements";
import { buildConfirmParams, confirmPaymentIntent } from "../helpers/stripe";
import type { MemberAddressState } from "../types";

type UpdateEndListener = () => void;

export interface ConfirmPaymentParams {
  memberAddress: MemberAddressState;
  paymentIntentClientSecret: string;
  paymentMethodType: string;
  returnUrl: string;
}

interface UseConfirmPaymentProps {
  communityProcessorPaymentMethodConfigurationId: string;
  doesCommunityProcessorSupportsLink: boolean;
  doesMemberHasDefaultPaymentMethod: boolean;
  isExpressMode: boolean;
  isMemberUsingDefaultPaymentMethod: boolean;
  isOneTimePrice: boolean;
  memberProcessorCustomerSessionClientSecret?: string;
}

/**
 * Stripe's Payment Element configured with a `payment_method_configuration` expects the underlying
 * payment intent to rely on `automatic_payment_methods`. Our payment intents are created with
 * explicit `payment_method_types`, so confirmation fails unless we temporarily remove the PMC and
 * set the explicit `payment_method_types` and `setup_future_usage` before confirming.
 */
export const useConfirmPayment = ({
  communityProcessorPaymentMethodConfigurationId,
  doesCommunityProcessorSupportsLink,
  doesMemberHasDefaultPaymentMethod,
  isExpressMode,
  isMemberUsingDefaultPaymentMethod,
  isOneTimePrice,
  memberProcessorCustomerSessionClientSecret,
}: UseConfirmPaymentProps) => {
  const getStripeElements = useGetStripeElements();
  const getStripeElementsOrNull = useGetStripeElementsOrNull();

  // Ref to store the listener function so we can remove it
  const updateEndListenerRef = useRef<UpdateEndListener | null>(null);

  // Cleanup listener if component unmounts while processing
  useEffect(
    () => () => {
      // Use OrNull variant to avoid throwing during unmount (e.g., error boundary recovery)
      const result = getStripeElementsOrNull();
      if (result && updateEndListenerRef.current) {
        result.stripeElements.off("update-end", updateEndListenerRef.current);
        updateEndListenerRef.current = null;
        addBugsnagBreadcrumb(
          "useConfirmPayment.elements.off('update-end') - cleaned up listener",
          { type: "manual" },
        );
      }
    },
    [getStripeElementsOrNull],
  );

  // Callback encapsulating the one-time execution logic
  const confirmPayment = useCallback(
    ({
      memberAddress,
      paymentIntentClientSecret,
      paymentMethodType,
      returnUrl,
    }: ConfirmPaymentParams) =>
      new Promise<boolean>((resolve, reject) => {
        const { stripe, stripeElements } = getStripeElements(
          "useConfirmPayment.confirmPayment",
        );

        // Express mode: skip element reconfiguration, confirm directly with saved payment method.
        if (isExpressMode) {
          const handleExpressMode = async () => {
            try {
              const confirmParams = buildConfirmParams(
                memberAddress,
                returnUrl,
              );
              await confirmPaymentIntent({
                caller: "useConfirmPayment.confirmPayment.expressMode",
                confirmParams,
                paymentIntentClientSecret,
                stripe,
                stripeElements: undefined, // stripeElements intentionally undefined - forces use of saved payment method
              });
              resolve(true);
            } catch (e) {
              reject(
                CheckoutError.fromUnknownError(
                  "useConfirmPayment.confirmPayment.expressMode",
                  e,
                ),
              );
            }
          };
          void handleExpressMode();
          return;
        }

        const handleUpdateEnd = async () => {
          // --- One-Time Execution Logic ---
          // 1. Do nothing if there is no listener (safeguard against multiple executions)
          if (!updateEndListenerRef.current) {
            return;
          }

          // 2. Immediately remove the listener to prevent multiple executions
          stripeElements.off("update-end", updateEndListenerRef.current);
          updateEndListenerRef.current = null; // Clear the ref
          addBugsnagBreadcrumb(
            "useConfirmPayment.confirmPayment.elements.off('update-end') - removed listener",
            { type: "manual" },
          );

          // 3. Execute the payment intent confirmation
          let error: CheckoutError | undefined;
          try {
            const confirmParams = buildConfirmParams(memberAddress, returnUrl);
            await confirmPaymentIntent({
              caller: "useConfirmPayment.confirmPayment",
              confirmParams,
              paymentIntentClientSecret,
              stripe,
              stripeElements,
            });
          } catch (e) {
            error = CheckoutError.fromUnknownError(
              "useConfirmPayment.confirmPayment",
              e,
            );
          } finally {
            // Restore the options to their original state in case of error
            const options: StripeElementsUpdateOptions = {
              customerSessionClientSecret:
                memberProcessorCustomerSessionClientSecret,
              payment_method_configuration:
                communityProcessorPaymentMethodConfigurationId,
              payment_method_types: null,
              setup_future_usage: null,
            };
            addBugsnagBreadcrumb(
              "useConfirmPayment.elements.update - restored original options",
              { metadata: { ...options }, type: "manual" },
            );
            stripeElements.update(options);
          }

          if (error) {
            reject(error);
            return;
          }

          resolve(true);
          // --- End One-Time Execution Logic ---
        };
        const updateEndListener: UpdateEndListener = () => {
          void handleUpdateEnd();
        };

        // --- One-Time Execution Logic ---
        // Store the listener function in the ref *before* attaching it
        updateEndListenerRef.current = updateEndListener;

        // Attach the listener *before* calling update
        addBugsnagBreadcrumb(
          "useConfirmPayment.elements.on('update-end') - attached listener",
          { type: "manual" },
        );
        stripeElements.on("update-end", updateEndListener);

        // Configure Elements options for submission
        const customerSessionClientSecret =
          resolveStripeElementsCustomerSessionClientSecret(
            memberProcessorCustomerSessionClientSecret,
            paymentMethodType,
            doesMemberHasDefaultPaymentMethod,
            isMemberUsingDefaultPaymentMethod,
          );
        const payment_method_types = resolveStripeElementsPaymentMethodTypes(
          doesCommunityProcessorSupportsLink,
          paymentMethodType,
        );
        const setup_future_usage = resolveStripeElementsSetupFutureUsage(
          paymentMethodType,
          isOneTimePrice,
        );
        const options: StripeElementsUpdateOptions = {
          customerSessionClientSecret,
          payment_method_configuration: null, // Temporarily disable PMC
          payment_method_types,
          setup_future_usage,
        };

        // Trigger the update - the 'update-end' event will fire after this completes
        addBugsnagBreadcrumb(
          "useConfirmPayment.elements.update - triggered 'update-end' event",
          { metadata: { ...options }, type: "manual" },
        );
        stripeElements.update(options);
      }),
    [
      communityProcessorPaymentMethodConfigurationId,
      doesCommunityProcessorSupportsLink,
      doesMemberHasDefaultPaymentMethod,
      getStripeElements,
      isExpressMode,
      isMemberUsingDefaultPaymentMethod,
      isOneTimePrice,
      memberProcessorCustomerSessionClientSecret,
    ],
  );

  return confirmPayment;
};
